import { useState } from 'react';
import {
  Box,
  FieldSet,
  FormField,
  Heading,
  Label,
  LabeledControl,
  Paragraph,
  Select,
  Slider,
  Spinner,
  Switch,
} from '@zillow/constellation-10';
import type { CompetitorMapDisplayMode } from '../../SharedComponents/Map/Dots/Dots';
import type { PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';
import { useAuth } from '../../../AuthProvider';
import {
  emitMarketCompsHideNewConstructionEvent,
  emitMarketCompsMapSliderEvent,
  emitMarketCompsSelectedCompetitorsOnlyEvent,
} from '../../../analytics/events';

type MarketCompsMapControlsProps = {
  selectedProperty: PortfolioAnalyticsPropertyResponseDto;
  hasPropertySelected: boolean;
  radius: number;
  setRadius: (radius: number) => void;
  showAllComps: boolean;
  setShowAllComps: (show: boolean) => void;
  showNewConstruction: boolean;
  setShowNewConstruction: (show: boolean) => void;
  isLoadingNewConstructions: boolean;
  mapDisplayMode: CompetitorMapDisplayMode;
  setMapDisplayMode: (mode: CompetitorMapDisplayMode) => void;
};

export const MarketCompsMapControls = ({
  selectedProperty,
  hasPropertySelected,
  radius,
  setRadius,
  showAllComps,
  setShowAllComps,
  showNewConstruction,
  setShowNewConstruction,
  isLoadingNewConstructions,
  mapDisplayMode,
  setMapDisplayMode,
}: MarketCompsMapControlsProps) => {
  const { user } = useAuth();
  const [localRadius, setLocalRadius] = useState(radius);
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleRadiusChange = (value: Array<number>) => {
    setLocalRadius(value[0]);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(
      setTimeout(() => {
        if (user) emitMarketCompsMapSliderEvent({ user });
        setRadius(value[0]);
      }, 500),
    );
  };

  const { street, city, state, zip, listingName } = selectedProperty.propertyDetails;
  const address = `${street}, ${city}, ${state} ${zip}`;

  return (
    <Box
      css={{
        position: 'absolute',
        top: '32px',
        left: '32px',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: '600',
        padding: '600',
        width: '280px',
        backgroundColor: 'token(colors.bg.elevated.smallShadow)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        overflowY: 'auto',
      }}
    >
      <Box>
        <Heading
          level={5}
          css={{ overflow: 'hidden' }}
          style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
        >
          {listingName}
        </Heading>
        <Paragraph css={{ marginTop: '200', fontSize: '14px' }}>{address}</Paragraph>
      </Box>

      <FormField
        label={
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Label>Search radius</Label>
            <Label>{localRadius} mi</Label>
          </Box>
        }
      >
        <Slider
          min={1}
          max={10}
          step={1}
          value={[localRadius]}
          onValueChange={handleRadiusChange}
          disabled={!hasPropertySelected}
        />
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Label>1 mi</Label>
          <Label>10 mi</Label>
        </Box>
      </FormField>

      <FormField label={<Label htmlFor="map-display-select">Map display</Label>}>
        <Select
          id="map-display-select"
          value={mapDisplayMode}
          size="sm"
          onChange={(e) => setMapDisplayMode(e.target.value as CompetitorMapDisplayMode)}
          disabled={!hasPropertySelected}
        >
          <option value="default">Default</option>
          <option value="specialOffer">Special offers</option>
          <option value="lowPrice">Low price</option>
          <option value="highPrice">High price</option>
          <option value="viewsRank">Views rank</option>
          <option value="leadsRank">Leads rank</option>
        </Select>
      </FormField>
      <FieldSet>
        <>
          <LabeledControl
            control={
              <Switch
                checked={!showAllComps}
                onChange={() => {
                  if (user) emitMarketCompsSelectedCompetitorsOnlyEvent({ user });
                  setShowAllComps(!showAllComps);
                }}
                disabled={!hasPropertySelected}
              />
            }
            label={<Label size="md">Selected competitors only</Label>}
          />

          <LabeledControl
            control={
              <Switch
                checked={showNewConstruction}
                onChange={() => {
                  if (user) emitMarketCompsHideNewConstructionEvent({ user });
                  setShowNewConstruction(!showNewConstruction);
                }}
                disabled={!hasPropertySelected}
              />
            }
            label={
              <Label>
                New construction
                {isLoadingNewConstructions && <Spinner size="sm" />}
              </Label>
            }
          />
        </>
      </FieldSet>
    </Box>
  );
};
